'use client'

import CardVertical from '@/components/hotels/CardVertical';
import { provider1FormatHotel } from '@/components/hotels/utils/provider1FormatHotel';
import ImageLoading from '@/components/ImageLoading';
import { useModalsControllersStore } from '@/core/stores/hotel';
import { useUserStore } from '@/core/stores/user';
import { useOutsideClick } from "@/hooks/useOutsideClick";
import useSetSearchParams from '@/hooks/useSetSearchParams';
import useSizeController from '@/hooks/useSizeController';
import { Provider1HotelType } from '@/types/provider1';
import getNextDate from '@/utils/getNextDay';
import generateItinerary from "@/utils/loading/loading.json";
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from "framer-motion";
import Cookies from 'js-cookie';
import Lottie from 'lottie-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const FavouredHotelsModal = () => {

    const [isReady, setIsReady] = useState(false)

    const { showFavouredHotelsModal, setShowFavouredModal } = useModalsControllersStore()
    const [hotels, setHotels] = useState<Provider1HotelType[]>([]);
    const { favoriteHotelsDetails } = useUserStore()
    const [activeCity, setActiveCity] = useState('')
    const { addQuey, params } = useSetSearchParams()

    const { back } = useRouter()

    const { isSmall } = useSizeController()

    const formattedIdentifier = `modal-favorite-hotels`

    const queryIdentifier = params[formattedIdentifier]

    useEffect(() => {
        if (!queryIdentifier) {
            setShowFavouredModal(false)
        }
    }, [queryIdentifier])

    useEffect(() => {
        if (favoriteHotelsDetails && Object.keys(favoriteHotelsDetails).length > 0) {
            const cityWithHotels = Object.entries(favoriteHotelsDetails).find(([Key, value]) => value.length > 0)
            cityWithHotels && setActiveCity(cityWithHotels[0])
        }
    }, [favoriteHotelsDetails, showFavouredHotelsModal])

    const ref = useOutsideClick(() => setShowFavouredModal(false));

    const [isGetting, setIsGetting] = useState(false)

    useEffect(() => {
        if (favoriteHotelsDetails) {
            setHotels(favoriteHotelsDetails[activeCity])
        }
    }, [showFavouredHotelsModal, activeCity]);

    useEffect(() => {
        if (showFavouredHotelsModal && typeof window !== 'undefined') {
            // Restore default styles when isReady is true
            isSmall && addQuey(formattedIdentifier)
            document.documentElement.classList.add("overflow-hidden");
            // document.body.classList.add("overflow-hidden");
        } else if (typeof window !== 'undefined') {
            document.documentElement.classList.remove("overflow-hidden");
            document.body.classList.remove("overflow-hidden");
        }
    }, [showFavouredHotelsModal]);

    useEffect(() => {
        if (typeof window !== 'undefined' && document) {
            setIsReady(true)
        }
    }, [])

    if (!isReady) {
        return null;
    }
    const currency = Cookies.get('currency') || 'USD'

    return showFavouredHotelsModal && isReady && createPortal(
        <AnimatePresence>
            {showFavouredHotelsModal && (
                <>
                    <motion.div className="fixed bottom-0 left-0 right-0 top-0 z-[100] before:absolute before:right-0 before:top-0 before:z-10 before:h-full before:w-full before:bg-gray-700/50">
                        {" "}
                    </motion.div>
                    <motion.div
                        ref={ref}
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1, transition: { delay: 0.2 } }}
                        exit={{ y: 50, opacity: 0 }}
                        transition={{
                            duration: 0.3,
                            type: "inertia",
                        }}
                        className="fixed bottom-0 left-0 right-0 top-0 z-[5006] m-auto w-full h-full max-h-dvh md:h-fit max-w-[512px] md:rounded-2xl bg-white  overflow-y-auto"
                    >
                        <div className="flex py-3 px-4 md:py-4 flex-row z-40 justify-between shadow md:shadow-none bg-white sticky top-0 left-0 md:relative">
                            <div className='flex items-center gap-3'>
                                <button>
                                    <FontAwesomeIcon onClick={() => {
                                        isSmall && back()
                                        setShowFavouredModal(false)
                                    }} icon={faArrowLeft} />
                                </button>

                                <h3 className="text-sm font-semibold text-gray-800 capitalize">
                                    Favorite Hotels
                                </h3>
                            </div>

                        </div>
                        <div className='flex max-w-full gap-4 px-3 items-end  border-b overflow-x-auto no-scroller mt-2'>
                            {
                                favoriteHotelsDetails && Object.keys(favoriteHotelsDetails).map(item => favoriteHotelsDetails[item].length > 0 && <button key={item}
                                    onClick={() => setActiveCity(item)}
                                    className={`${activeCity === item ? 'text-indigo-500 border-indigo-500' : 'border-white text-neutral-400'}
                                                    capitalize pb-1 text-xs border-b px-1 flex gap-1 min-w-max`}
                                >
                                    <span>{item}</span>
                                    <span>({favoriteHotelsDetails[item].length})</span>
                                </button>
                                )
                            }
                        </div>

                        <div className='p-4 flex flex-col gap-3'>
                            {!isGetting ? (hotels && hotels.length > 0) ? hotels?.map(hotel => <CardVertical
                                key={hotel.id}
                                onNavigate={() => setShowFavouredModal(false)}
                                navigateTo={provider1FormatHotel(hotel, 2, currency).navigateTo({ start: getNextDate(0), end: getNextDate(1), passenger: 'a1-c-1' })}
                                hotel={provider1FormatHotel(hotel, 2, currency)} />) :
                                <div className="flex flex-col items-center pt-10">
                                    <ImageLoading
                                        className="mx-auto w-48 h-48"
                                        src={`assets/ac7d899a-835f-4c04-b724-86abae85d198-planingo.png`}
                                        width={300}
                                        height={300}
                                        quality={65}
                                        alt="Nothing Found"
                                    />
                                    <p className='text-gray-800 text-xl mt-3'>Your saved stays are waiting to happen</p>
                                    <p className='text-gray-600 font-normal text-center text-base mt-3'>Haven’t saved any hotels yet? Tap the heart and start building your dream list.</p>
                                </div>

                                : <div className='grow flex items-center justify-center'>
                                    <Lottie
                                        className="max-w-64"
                                        animationData={generateItinerary}
                                        loop={true}
                                    />
                                </div>}
                        </div>


                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        window.document.body,
    );
};
export default FavouredHotelsModal;
